import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Snowflake, ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, Clock,
  DollarSign, Users, Database, Server, Search, Loader2, RefreshCw,
  FileText, Lightbulb, CheckCircle, XCircle, Zap, BarChart3
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import SnowflakeConnect from '../components/SnowflakeConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpikeAutopsy = () => {
  const [loading, setLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [autopsyData, setAutopsyData] = useState(null);
  const { credentials, isConnected, updateCredentials } = useSnowflakeCredentials();
  const { shouldShowData } = useDemoData();

  const fetchAutopsy = async () => {
    if (!credentials) {
      setShowConnect(true);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/snowflake/advanced/spike-autopsy`, {
        credentials,
        threshold_percent: 50
      });
      setAutopsyData(response.data);
      toast.success('Spike autopsy report generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchAutopsy();
    }
  }, [isConnected]);

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  // Mock data for demo
  const mockData = {
    spike_detected: true,
    summary: {
      spike_date: new Date().toISOString().split('T')[0],
      credits_today: 2341,
      credits_average: 1000,
      spike_percent: 134.1,
      threshold_percent: 50
    },
    daily_trend: [
      { DAY: 'Mon', CREDITS: 980 },
      { DAY: 'Tue', CREDITS: 1020 },
      { DAY: 'Wed', CREDITS: 1100 },
      { DAY: 'Thu', CREDITS: 2341 },
      { DAY: 'Fri', CREDITS: 1200 },
      { DAY: 'Sat', CREDITS: 600 },
      { DAY: 'Sun', CREDITS: 400 },
    ],
    warehouse_breakdown: [
      { WAREHOUSE_NAME: 'ANALYTICS_WH', CREDITS: 1456, ACTIVE_HOURS: 18 },
      { WAREHOUSE_NAME: 'ETL_WH', CREDITS: 534, ACTIVE_HOURS: 24 },
      { WAREHOUSE_NAME: 'REPORTING_WH', CREDITS: 234, ACTIVE_HOURS: 12 },
    ],
    top_users: [
      { USER_NAME: 'john.smith@company.com', QUERY_COUNT: 234, CREDITS: 876, TOTAL_MINUTES: 456 },
      { USER_NAME: 'data_team@company.com', QUERY_COUNT: 567, CREDITS: 432, TOTAL_MINUTES: 234 },
      { USER_NAME: 'analytics_svc', QUERY_COUNT: 1234, CREDITS: 234, TOTAL_MINUTES: 123 },
    ],
    top_queries: [
      { QUERY_ID: 'Q-7834', QUERY_TEXT: 'SELECT * FROM fact_sales JOIN dim_products...', USER_NAME: 'john.smith@company.com', WAREHOUSE_NAME: 'ANALYTICS_WH', CREDITS: 234.5, DURATION_SECONDS: 2723 },
      { QUERY_ID: 'Q-7821', QUERY_TEXT: 'CREATE TABLE analytics.monthly_report AS...', USER_NAME: 'data_team@company.com', WAREHOUSE_NAME: 'ETL_WH', CREDITS: 187.2, DURATION_SECONDS: 1931 },
    ],
    recommendations: [
      "Review warehouse 'ANALYTICS_WH' - it's consuming the most credits today",
      "User 'john.smith@company.com' ran the most expensive queries - consider reviewing their workload",
      "Consider optimizing the top queries by adding clustering keys or filters",
      "Set up automated alerts to catch spikes earlier",
      "Consider scheduling heavy workloads during off-peak hours"
    ]
  };

  const displayData = autopsyData || (shouldShowData ? mockData : null);

  if (!displayData) {
    return (
      <div className="space-y-6" data-testid="spike-autopsy">
        <EmptyDataState title="Spike Autopsy" description="Connect your Snowflake account to analyze cost spikes and get root cause analysis." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="spike-autopsy">
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <FileText className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Spike Autopsy</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Automatic incident report when costs spike</p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isConnected && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">Demo Data</Badge>
          )}
          <Button onClick={fetchAutopsy} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analyzing...' : 'Run Autopsy'}
          </Button>
        </div>
      </div>

      {/* Spike Alert Banner */}
      {displayData.spike_detected && (
        <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">
                SPIKE DETECTED: {displayData.summary.spike_percent.toFixed(1)}% Above Average
              </h3>
              <p className="text-slate-400 dark:text-slate-400 mb-4">
                Today's credit usage ({displayData.summary.credits_today.toLocaleString()} credits) is significantly higher 
                than the 7-day average ({displayData.summary.credits_average.toLocaleString()} credits).
              </p>
              <div className="flex gap-4">
                <div className="px-4 py-2 rounded-lg bg-white/50 dark:bg-slate-800/50">
                  <p className="text-sm text-slate-400">Today's Credits</p>
                  <p className="text-2xl font-bold text-red-600">{displayData.summary.credits_today.toLocaleString()}</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/50 dark:bg-slate-800/50">
                  <p className="text-sm text-slate-400">7-Day Average</p>
                  <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">{displayData.summary.credits_average.toLocaleString()}</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/50 dark:bg-slate-800/50">
                  <p className="text-sm text-slate-400">Spike %</p>
                  <p className="text-2xl font-bold text-red-600">+{displayData.summary.spike_percent.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Trend */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-500" />
            Daily Credit Trend
          </CardTitle>
          <CardDescription>Credit usage over the past 7 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
              <AreaChart data={displayData.daily_trend}>
                <defs>
                  <linearGradient id="creditGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="DAY" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                  formatter={(value) => [`${value.toLocaleString()} credits`, 'Credits']}
                />
                <Area type="monotone" dataKey="CREDITS" stroke="#ef4444" strokeWidth={2} fill="url(#creditGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Warehouse Breakdown */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-emerald-500" />
              Warehouse Breakdown
            </CardTitle>
            <CardDescription>Which warehouses consumed the most</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.warehouse_breakdown.map((wh, idx) => (
                <div key={wh.WAREHOUSE_NAME} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold ${
                        idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-amber-500' : 'bg-slate-400'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="font-medium text-slate-900 dark:text-white">{wh.WAREHOUSE_NAME}</span>
                    </div>
                    <span className="font-bold text-lg text-slate-900 dark:text-white">{(wh.CREDITS || 0).toLocaleString()} credits</span>
                  </div>
                  <div className="text-sm text-slate-400">Active for {wh.ACTIVE_HOURS} hours</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              Top Users by Cost
            </CardTitle>
            <CardDescription>Who ran the most expensive queries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.top_users.map((user, idx) => (
                <div key={user.USER_NAME} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{user.USER_NAME}</span>
                    <span className="font-bold text-slate-900 dark:text-white">{(user.CREDITS || 0).toFixed(1)} credits</span>
                  </div>
                  <div className="text-sm text-slate-400">
                    {user.QUERY_COUNT} queries • {Math.round(user.TOTAL_MINUTES || 0)} min runtime
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Expensive Queries */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-amber-500" />
            Top Expensive Queries
          </CardTitle>
          <CardDescription>The queries that caused the spike</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {displayData.top_queries.map((query) => (
              <div key={query.QUERY_ID} className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{query.QUERY_ID}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">{query.WAREHOUSE_NAME}</Badge>
                  </div>
                  <span className="font-bold text-lg text-amber-600">${((query.CREDITS || 0) * 2.5).toFixed(2)}</span>
                </div>
                <p className="text-sm font-mono text-slate-400 dark:text-slate-400 truncate mb-2">
                  {query.QUERY_TEXT}
                </p>
                <div className="flex gap-4 text-xs text-slate-400">
                  <span><Users className="w-3 h-3 inline mr-1" />{query.USER_NAME}</span>
                  <span><Clock className="w-3 h-3 inline mr-1" />{Math.round((query.DURATION_SECONDS || 0) / 60)} min</span>
                  <span><Zap className="w-3 h-3 inline mr-1" />{(query.CREDITS || 0).toFixed(1)} credits</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-emerald-500" />
            Recommendations
          </CardTitle>
          <CardDescription>Actions to prevent future spikes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {displayData.recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">{rec}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpikeAutopsy;
